import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { ShowButton } from "@/components/admin/show-button";

import { formatDealAmount } from "../misc/formatAmount";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { AsideSection } from "../misc/AsideSection";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { resourceForContact } from "./contactResource";

/**
 * Shows budget (`amount`) and case description, stored directly on the
 * contact (the contact page is the single page of the case it represents).
 * The amount row always renders (with the shared placeholder when unset);
 * the description row only when filled.
 */
export const ContactCaseInfo = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const { currency } = useConfigurationContext();

  if (!record) return null;

  return (
    <AsideSection
      title={translate("resources.contacts.field_categories.case_info")}
    >
      <div>
        <div className="text-xs text-muted-foreground tracking-wide">
          {translate("resources.contacts.fields.amount")}
        </div>
        <div>{formatDealAmount(record.amount, currency)}</div>
      </div>
      {record.description && (
        <div>
          <div className="text-xs text-muted-foreground tracking-wide">
            {translate("resources.contacts.fields.description")}
          </div>
          <div className="whitespace-pre-wrap">{record.description}</div>
        </div>
      )}
    </AsideSection>
  );
};

export const ContactAside = ({
  link = "edit",
  showTasks = true,
}: {
  link?: "edit" | "show";
  showTasks?: boolean;
}) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 text-sm">
      <div className="mb-4 -ml-1">
        {link === "edit" ? (
          <EditButton
            label={`resources.${resourceForContact(record)}.action.edit`}
          />
        ) : (
          <ShowButton
            label={`resources.${resourceForContact(record)}.action.show`}
          />
        )}
      </div>

      <AsideSection title={translate("resources.notes.fields.status")}>
        <ContactStatusSelector />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      <ContactCaseInfo />

      <AsideSection
        title={translate("resources.tags.name", { smart_count: 2 })}
      >
        <TagsListEdit />
      </AsideSection>

      {showTasks && (
        <AsideSection
          title={translate("resources.tasks.name", { smart_count: 2 })}
        >
          <ReferenceManyField
            target="contact_id"
            reference="tasks"
            sort={{ field: "due_date", order: "ASC" }}
            perPage={1000}
          >
            <TasksIterator />
          </ReferenceManyField>
          <AddTask />
        </AsideSection>
      )}

      {link !== "edit" && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </>
      )}
    </div>
  );
};
